import { useState } from "react";
import useTranslator from "../../../hooks/useTranslator";
import { formatDateAndTime, getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import GridCard from "../../../components/shared/gridCard/GridCard";
import DashboardSection from "../../dashboard/components/DashboardSection";
import "../../dashboard/components/dashboard-sections.css";
import { MixingIconSvg } from "../../../svgFiles/MixingIconSvg";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import KgGreen from "../../../images/kg_green.svg";
import { useGetMixingSummaryQuery, useGetPastingSummaryQuery } from "../mixingApi";

const PastingDashboard = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    const single = start === end;
    setStartDate(start);
    setEndDate(end);
    if (single) {
      setLotNumber(makePriorLotNumber(start));
    }
  };

  // Mixing Summary Query
  const { data: mixingSummaryData } = useGetMixingSummaryQuery({ 
    start_date: startDate,
    end_date: endDate,
    batch_code: lotNumber 
  });

  // Pasting Summary Query
  const { data: pastingSummaryData } = useGetPastingSummaryQuery({ 
    start_date: startDate,
    end_date: endDate,
    batch_code: lotNumber 
  });

  // Mixing Overview Items - Color coded boxes
  const mixingListItems = [
    {
      label: _t("total_entered_weight", "Total Entered Weight"),
      value: mixingSummaryData?.data?.total_entered_weight ?? 0,
      textColor: "#60a5fa",
    },
    {
      label: _t("total_cheeks_entered", "Total Cheeks Entered"),
      value: mixingSummaryData?.data?.total_cheeks_entered ?? 0,
      textColor: "#4ade80",
    },
    {
      label: _t("total_strips_entered", "Total Strips Entered"),
      value: mixingSummaryData?.data?.total_strips_entered ?? 0,
      textColor: "#fb923c",
    },
    {
      label: _t("total_entered_from_brokerage", "Total KG From Brokerage"),
      value: mixingSummaryData?.data?.total_entered_from_brokerage ?? 0,
      textColor: "#f472b6",
    },
  ];

  // Pasting Overview Items - Color coded boxes
  const pastingListItems = [
    {
      label: _t("total_weight", "Total Weight"),
      value: pastingSummaryData?.data?.total_weight ?? 0,
      textColor: "#60a5fa",
    },
    {
      label: _t("packed_weight", "Weight Packed"),
      value: pastingSummaryData?.data?.packed_weight ?? 0,
      textColor: "#4ade80",
    },
    {
      label: _t("unpacked_weight", "Weight Not Packed"),
      value: pastingSummaryData?.data?.unpacked_weight ?? 0,
      textColor: "#f87171",
    },
    {
      label: _t("total_boxes", "Total Boxes"),
      value: pastingSummaryData?.data?.total_boxes ?? 0,
      textColor: "#fb923c",
    },
  ];

  return (
    <div>
      {/* ── Toolbar with Date & Lot Filter ──────────────────────── */}
      <div className="d-flex gap-2 align-items-center mb-4" style={{ justifyContent: "space-between" }}>
        <h5 className="m-0">{_t("pasting_mixing_dashboard", "Pasting & Mixing Dashboard")}</h5>
        <div className="d-flex gap-3 align-items-center">
          <div className="d-flex gap-2 align-items-center">
            <span className="fw-600" style={{ fontSize: "14px" }}>
              {_t("batch_code", "Batch Code")}:
            </span>
            <span
              style={{
                background: "#fff0ea",
                color: "#ee7225",
                padding: "6px 12px",
                borderRadius: "6px",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              #{lotNumber ?? 0}
            </span>
          </div>
          <DateRangeFilter onApplyRange={handleApplyRange} triggerId="pasting-dashboard-date-range-filter" />
        </div>
      </div>

      <div className="d-flex flex-column gap-4">
        {/* ── Mixing Overview Accordion ──────────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><MixingIconSvg size={18} color="#60a5fa" />{_t("mixing_overview", "Mixing Overview")}</span>}>
          <div className="pp-brokering-grid">
            {mixingListItems.map((item, i) => (
              <div
                key={i}
                className="pp-brokering-box"                
              >
                <div className="pp-brokering-box-label" style={{ color: item.textColor }}>
                  {item.label}
                </div>
                <div className="pp-brokering-box-value" style={{ color: item.textColor }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </DashboardSection>

        {/* ── Pasting Overview Accordion ──────────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><PackageIconSvg size={18} color="#60a5fa" />{_t("pasting_overview", "Pasting Overview")}</span>}>
          <div className="db-trash-grid" style={{ padding: "12px 18px" }}>
            {pastingListItems.map((item, i) => {
              const isTotal = /total/i.test(item.label);
              return (
                <div
                  key={i}
                  className="db-trash-cell"
                  style={
                    isTotal
                      ? {
                          background: "#fff8f5",
                          borderColor: "#f5d9c8",
                          borderRadius: "6px",
                        }
                      : {
                          borderRadius: "6px",
                        }
                  }
                >
                  <span style={{ color: item.textColor, fontWeight: "500" }}>
                    {item.label}
                  </span>
                  <span
                    className={`db-trash-val ${
                      /packed/i.test(item.label)
                        ? "green"
                        : /not packed/i.test(item.label)
                        ? "red"
                        : isTotal
                        ? "orange"
                        : "blue"
                    }`}
                    style={{ color: item.textColor, fontWeight: "600" }}
                  >
                    {item.value}
                  </span>
                </div>
              );
            })}
          </div>
        </DashboardSection>
      </div>
    </div>
  );
};

export default PastingDashboard;
